import React, { useEffect, useState } from "react";
import { Editor as EditorRef } from "@tinymce/tinymce-react";
import { useTranslation } from "react-i18next";
import { SSMLTag, SSMLTagsColors } from "shared/constants";
import {
  handleSSMLValueClick,
  isEditorEmpty,
  useEditorSelection,
} from "shared/utils/tts-editor-helper";
import { useIsActiveSSMLTag } from "shared/hooks/useSSMLTag";
import { getCurrentSSMLValue } from "features/TTS/TTSCustomEditor/utils/editorKeyDownHandler";
import DataObjectIcon from "@mui/icons-material/DataObject";
import { useSSMLMenuDisabled } from "shared/utils/menu-disabling-rules";
import PronunciationMenuTabs from "../PronunciationMenu/PronunciationMenuTabs";
import CustomIconTextButtonMenu from "../../components/CustomIconTextButtonMenu";

interface PronunciationMenuProps {
  editorRef: React.RefObject<EditorRef | null>;
  disabled?: boolean;
}

export default function PronunciationMenu({
  editorRef,
  disabled,
}: PronunciationMenuProps): JSX.Element {
  const { t } = useTranslation();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [userPhonemeIPA, setUserPhonemeIPA] = useState<string>("");
  const [userPhonemeXSampa, setUserPhonemeXSampa] = useState<string>("");
  const [selectedWord, setSelectedWord] = useState<string>("");
  const [originalPhonemeValue, setOriginalPhonemeValue] = useState<string>("");

  const isPhonemeTagActive = useIsActiveSSMLTag(SSMLTag.Phoneme);
  const editorEmpty = isEditorEmpty(editorRef);
  const hasSelection = useEditorSelection(editorRef);

  const isSelectionDisabled = useSSMLMenuDisabled(editorRef, SSMLTag.Phoneme);

  const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTabIndex(newValue);
  };

  const handleDialogClose = () => {
    if (!isPhonemeTagActive) {
      setUserPhonemeIPA("");
      setUserPhonemeXSampa("");
      setSelectedTabIndex(0);
    }
  };

  useEffect(() => {
    const updateSelectedWordAndPhoneme = () => {
      const editor = editorRef?.current?.editor;
      if (!editor || !editor.selection) return;

      if (isPhonemeTagActive) {
        const selectedNode = editor.selection.getNode();
        if (!selectedNode) return;

        const phonemeNode = selectedNode.closest(
          `[data-ssml^="${SSMLTag.Phoneme}"]`
        );
        if (phonemeNode) {
          const text =
            (phonemeNode as HTMLElement).innerText
              ?.replace(/[{}]/g, "")
              .trim() || "";
          setSelectedWord(text);

          const currentValue = getCurrentSSMLValue(editorRef, SSMLTag.Phoneme);
          if (currentValue) {
            const [alphabet, phonemeValue] = currentValue.split(", ");
            setOriginalPhonemeValue(phonemeValue);

            if (alphabet === "ipa") {
              setSelectedTabIndex(0);
              setUserPhonemeIPA(phonemeValue);
              setUserPhonemeXSampa("");
            } else if (alphabet === "x-sampa") {
              setSelectedTabIndex(1);
              setUserPhonemeXSampa(phonemeValue);
              setUserPhonemeIPA("");
            }
          }
        }
      } else {
        const selectedText =
          editor.selection
            .getContent({ format: "text" })
            ?.replace(/[{}]/g, "")
            .trim() || "";
        setSelectedWord(selectedText);
        setOriginalPhonemeValue("");
      }
    };

    const editorInstance = editorRef?.current?.editor;

    if (editorInstance) {
      editorInstance.on("NodeChange", updateSelectedWordAndPhoneme);
      updateSelectedWordAndPhoneme();

      return () => {
        editorInstance.off("NodeChange", updateSelectedWordAndPhoneme);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPhonemeTagActive]);

  const handleApplyClick = () => {
    if (!editorRef?.current || selectedWord === "") return;

    const alphabet = selectedTabIndex === 0 ? "ipa" : "x-sampa";
    const phValue = selectedTabIndex === 0 ? userPhonemeIPA : userPhonemeXSampa;
    handleSSMLValueClick(
      editorRef,
      SSMLTag.Phoneme,
      `${alphabet}, ${phValue}`, // pass comma-separated values for the attributes
      SSMLTagsColors.PronunciationTag,
      []
    );
  };

  const hasChanges =
    (selectedTabIndex === 0 && userPhonemeIPA !== originalPhonemeValue) ||
    (selectedTabIndex === 1 && userPhonemeXSampa !== originalPhonemeValue);

  const isDisabled =
    disabled ||
    editorEmpty ||
    isSelectionDisabled ||
    (!hasSelection && !isPhonemeTagActive);

  return (
    <CustomIconTextButtonMenu
      useDialog
      componentId="pronunciation"
      menuTitle={t("tts.toolbarMenu.pronunciationMenu.title")}
      dialogHeader={t("tts.toolbarMenu.pronunciationMenu.title")}
      onDialogPrimaryButtonClick={handleApplyClick}
      isDialogPrimaryButtonDisabled={
        selectedWord === "" ||
        (!hasChanges && isPhonemeTagActive) ||
        (selectedTabIndex === 0 && !userPhonemeIPA) ||
        (selectedTabIndex === 1 && !userPhonemeXSampa)
      }
      onDialogClose={handleDialogClose}
      isActive={isPhonemeTagActive}
      isEditorEmpty={editorEmpty}
      shouldBeDisabled={isDisabled}
      showClearButton={isPhonemeTagActive}
      editorRef={editorRef}
      tagType={SSMLTag.Phoneme}
      icon={DataObjectIcon}
      isSelectionDisabled={isSelectionDisabled}
    >
      <PronunciationMenuTabs
        selectedTabIndex={selectedTabIndex}
        handleChangeTab={handleChangeTab}
        selectedWord={selectedWord}
        userPhonemeIPA={userPhonemeIPA}
        setUserPhonemeIPA={setUserPhonemeIPA}
        userPhonemeXSampa={userPhonemeXSampa}
        setUserPhonemeXSampa={setUserPhonemeXSampa}
      />
    </CustomIconTextButtonMenu>
  );
}
